import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { collection, setDoc, getDoc, doc } from "firebase/firestore";
import axios from "axios";
import { selectUser } from "../../store/userSlice";
import classes from "./CheckOutSummary.module.css";
import { Link } from "react-router-dom";
import { db, FUNCTIONS_BASE_URL } from "../../services/firebase";
import {
  errorNotification,
  infoNotification,
  warningNotification,
} from "../../utils/notifications";
import {
  removeItem,
  selectPreorderMaxQty,
  setCartItems,
} from "../../store/cartSlice";
import {
  formatExpectedDate,
  getOrderPreorderDetails,
} from "../../utils/preorder";
import { reconcileCartWithStock } from "../../utils/cartStock";
import {
  formatAddress,
  getDefaultAddress,
  getUserAddresses,
  toOrderUserDetail,
  validateAddress,
} from "../../utils/addresses";
import AddressSheet from "../Address/AddressSheet";
import {
  DEFAULT_DELIVERY_CHARGE,
  getDeliveryCharge,
} from "../../utils/logistics";

const CheckOutSummary = ({ cartItems, totalBookQuantity }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, userDetail } = useSelector(selectUser);
  const preorderMaxQty = useSelector(selectPreorderMaxQty);
  const preorderDetails = getOrderPreorderDetails(cartItems);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  // state update is async, ref blocks the second click immediately
  const placingOrderRef = useRef(false);
  const { id, phone } = userDetail;

  // delivery address - default address is selected, user can change it in the sheet
  const addresses = getUserAddresses(userDetail);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [isAddressSheetOpen, setIsAddressSheetOpen] = useState(false);
  const shippingAddress =
    addresses.find((item) => item.id === selectedAddressId) ||
    getDefaultAddress(addresses);
  const country = shippingAddress ? shippingAddress.country : "";

  // delivery charge for India - set by admin in settings
  const [delivery, setDelivery] = useState(DEFAULT_DELIVERY_CHARGE);
  const [isDeliveryLoaded, setIsDeliveryLoaded] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "app", "meta"))
      .then((docSnap) => setDelivery(getDeliveryCharge(docSnap.data())))
      .catch((e) => console.log("delivery charge: ", e))
      .finally(() => setIsDeliveryLoaded(true));
  }, []);

  const discount = 0;

  const subtotal = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.total_price, 0);
  }, [cartItems]);

  const total = useMemo(() => subtotal + delivery, [delivery, subtotal]);

  // items - cart items checked with the latest stock and pre-order details
  const addDataToOrdersCollection = async (items) => {
    let ordered_books = [];
    items.forEach((item) => {
      let temp = {
        id: item.id,
        author: item.author,
        genre: item.genre,
        qty: item.qty,
        title: item.title,
        title_tamil: item.title_tamil,
        mrp_price: item.mrp_price,
        item_price: item.discount_price,
        total_price: item.total_price,
        book_format: item.book_format,
        image: item.images[0],
        is_preorder: Boolean(item.is_preorder),
      };
      if (item.is_preorder) {
        temp.expected_delivery_date = item.expected_delivery_date || null;
        temp.preorder_remark = item.preorder_remark || "";
      }
      ordered_books.push(temp);
    });

    let ordered_timestamp = new Date().getTime();

    // Generate order ID first so we can include it in the document
    const orderRef = doc(collection(db, "orders"));
    const orderId = orderRef.id;

    const order = {
      delivery_charge: delivery,
      logistics: "",
      order_id: orderId, // Store document ID inside the document for easy filtering
      ordered_books,
      ...getOrderPreorderDetails(ordered_books),
      ordered_timestamp,
      price_tax: 0,
      status: "booked",
      tax_percentage: 0,
      total_item_price: subtotal,
      total_price: total,
      // total_qty: ordered_books.length,
      total_qty: totalBookQuantity,
      // name, phone and address of the selected delivery address
      userDetail: toOrderUserDetail(userDetail, shippingAddress),
      shipping_address: {
        id: shippingAddress.id,
        label: shippingAddress.label,
      },
      // account which placed the order, delivery can be for someone else
      ordered_by: {
        name: userDetail.name,
        phone: userDetail.phone,
      },
      payment_method: "online-payment-gateway",
      payment_status: "PAYMENT_INITIATED",
    };

    // console.log("order: ", order, userDetail);

    try {
      await setDoc(orderRef, order);

      // console.log("orderId: ", orderId);

      const res = await axios.post(
        `${FUNCTIONS_BASE_URL}/phonepe/pay`,
        {
          amount: total,
          userId: id,
          mobile: phone,
          txnId: orderId,
        }
      );

      const result = res.data;

      if (result.success) {
        // console.log(
        //   "result.data: ",
        //   result.data,
        //   result.data.instrumentResponse.redirectInfo.url
        // );
        infoNotification(result.message);
        window.location.replace(
          result.data.instrumentResponse.redirectInfo.url
        );
        // result.data
        // dispatch(clearCart());
        // successNotification("Order placed successfully!");
        // navigate("/orders");
      } else {
        // failed
        errorNotification(res.data.message);
      }
    } catch (e) {
      errorNotification(e.message);
    }
  };

  const handleCheckoutButton = async (e) => {
    e.preventDefault();
    if (placingOrderRef.current) {
      return;
    }
    if (isAuthenticated && !shippingAddress) {
      errorNotification("Please add a delivery address to place the order");
      setIsAddressSheetOpen(true);
      return;
    }
    if (isAuthenticated && validateAddress(shippingAddress)) {
      // old profile address might not have all the details
      errorNotification(
        `${validateAddress(shippingAddress)}. Please update your delivery address`
      );
      setIsAddressSheetOpen(true);
      return;
    }
    if (isAuthenticated) {
      let text = "Are you sure to proceed?";
      if (window.confirm(text) === true) {
        // check out of stock for all the items first, then place a single order
        placingOrderRef.current = true;
        setIsPlacingOrder(true);
        try {
          const result = await Promise.all(
            cartItems.map((item) => getDoc(doc(db, "books", item.id)))
          );

          const stockById = {};
          result.forEach((docSnap) => {
            if (docSnap.exists()) {
              stockById[docSnap.id] = docSnap.data();
            }
          });

          const { removed, adjusted, updatedCartItems, canPlaceOrder } =
            reconcileCartWithStock(cartItems, stockById, preorderMaxQty);

          removed.forEach((item) => {
            // out of stock, remove that item
            dispatch(removeItem(item));
            errorNotification(
              `${item.title} (${item.title_tamil}) is out of stock`
            );
          });

          if (adjusted.length > 0) {
            // stock is there but less the qty in cart, so re-calculate
            dispatch(setCartItems(updatedCartItems));
            warningNotification(
              `The selected number of quantity is not available lowering your quantity to available number`
            );
          }

          if (canPlaceOrder) {
            // every item is available, so buy it directly
            await addDataToOrdersCollection(updatedCartItems);
          }
        } catch (e) {
          errorNotification(e.message);
        } finally {
          placingOrderRef.current = false;
          setIsPlacingOrder(false);
        }
      }
    } else {
      // show login modal
      errorNotification("Please login to complete this order!!!");
    }
  };

  // console.log(subtotal, total);
  return (
    <>
      <div className={`${classes.summary} container`}>
        {isAuthenticated && (
          <div className={classes.deliverTo}>
            <div className={classes.deliverToDetails}>
              <p className={classes.deliverToTitle}>
                Deliver to
                {shippingAddress && (
                  <span className={classes.deliverToLabel}>
                    {shippingAddress.label}
                  </span>
                )}
              </p>
              {shippingAddress ? (
                <>
                  <p className={classes.deliverToName}>
                    {shippingAddress.name}, {shippingAddress.phone}
                  </p>
                  <p className={classes.deliverToAddress}>
                    {formatAddress(shippingAddress)}
                  </p>
                </>
              ) : (
                <p className={classes.deliverToAddress}>
                  No delivery address added yet.
                </p>
              )}
            </div>
            <button
              type="button"
              className={classes.deliverToBtn}
              onClick={() => setIsAddressSheetOpen(true)}
            >
              {shippingAddress ? "Change" : "Add Address"}
            </button>
            <AddressSheet
              open={isAddressSheetOpen}
              onClose={() => setIsAddressSheetOpen(false)}
              selectedId={shippingAddress ? shippingAddress.id : ""}
              onSelect={setSelectedAddressId}
            />
          </div>
        )}
        {country === "India" || country === "" ? (
          <div className={`${classes.CheckOutSummary} row`}>
            <div className={`${classes.CheckOutSummary1} col-md-6`}>
              <h3 className={classes.shoppingheading}>Shopping Summary</h3>
              <p className={classes.shoppingcontent}>
                Almost there! Your chosen books are ready to spark young
                imaginations. Review your selection and when you're all set,
                click 'Checkout' to confirm. Happy reading awaits!
              </p>
              {/* <p className={classes.shoppingcode}>Have a coupon code?</p>
            <from className={classes.text}>
              <i className="fa-solid fa-ticket"></i>
              <input
                type="text"
                placeholder="Enter promo code here"
                className={classes.promocodeinput}
              />
              <button className={classes.btncode}>
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </from> */}
            </div>
            <div className={`${classes.CheckOutSummary2} col-md-6`}>
              <div className={`${classes.total}`}>
                <p className={classes.subtotal}>Subtotal</p>
                <p className={classes.amount}>₹ {subtotal}</p>
              </div>
              {discount !== 0 && (
                <div className={`${classes.total}`}>
                  <p className={classes.subtotal}>Discount</p>
                  <p className={classes.amount}>₹ 0</p>
                </div>
              )}
              <div className={`${classes.total}`}>
                <p className={classes.subtotal}>Delivery (within India)</p>
                <p className={classes.amount}>
                  {isDeliveryLoaded ? `₹ ${delivery}` : "..."}
                </p>
              </div>
              {preorderDetails.has_preorder && (
                <p className={classes.preorderNote}>
                  Your cart has a pre-order book. The whole order will be
                  shipped together
                  {preorderDetails.expected_delivery_date
                    ? `, expected by ${formatExpectedDate(
                        preorderDetails.expected_delivery_date
                      )}.`
                    : " once the book is released."}
                </p>
              )}
              <hr />
              <div className={`${classes.total}`}>
                <p className={classes.subtotal}>Total</p>
                <p className={classes.amount}>
                  {isDeliveryLoaded ? `₹ ${total}` : "..."}
                </p>
              </div>
              <button
                className={classes.checkoutbtn}
                onClick={handleCheckoutButton}
                disabled={isPlacingOrder || !isDeliveryLoaded}
              >
                {isPlacingOrder ? "Please wait..." : "Checkout"}
              </button>
              <Link to="/books" className={classes.Continue}>
                {/* <a href="" > */}
                Continue Shopping
                {/* </a> */}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h3 className={classes.shoppingheading}>Shopping Summary</h3>
            <p className={classes.shoppingcontent}>
              Almost there! Your chosen books are ready to spark young
              imaginations. For countries outside India or for any bulk orders,
              please reach out to us by <a href="/contact-us">clicking here</a>.
              Happy reading awaits!
            </p>
          </>
        )}
      </div>
    </>
  );
};

export default CheckOutSummary;
